/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { Modal } from '../Modal'
import { EditableField } from '../EditableField'
import SelectUserGroup from '../SelectUserGroup'
import { useServiceContext } from '../../root'
import {
  getNotificationForFsi,
  saveNotificationForFsi,
  type FsiNotificationResponse
} from '../../services/api/notifications'
import { toast } from '../../services/toast'
import type { User, Group } from '../../services/api/users'

interface FsiNotificationsModalProps {
  show: boolean
  handleClose: () => void
  file: { id: number; name?: string; type?: string } | null
}

interface NotificationsState {
  user: (User | Group)[]
  system: (User | Group)[]
  applyRecursively: boolean
  applyOnlyChanges: boolean
}

const SYSTEM_HELPER_TYPE = {
  USER: 'USER',
  GROUP: 'GROUP'
}

const FILE_TYPE = {
  FOLDER: 'FOLDER',
  DOCUMENT: 'DOCUMENT'
}

export function FsiNotificationsModal({ show, handleClose, file }: FsiNotificationsModalProps) {
  const context = useServiceContext()
  const { translate } = context.service.filter

  const [availableUsersGroups, setAvailableUsersGroups] = useState<(User | Group)[]>([])
  const [notifications, setNotifications] = useState<NotificationsState>({
    user: [],
    system: [],
    applyRecursively: false,
    applyOnlyChanges: false
  })
  const [originalNotifications, setOriginalNotifications] = useState<NotificationsState>({
    user: [],
    system: [],
    applyRecursively: false,
    applyOnlyChanges: false
  })
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (show && file && file.id) {
      init()
    }
  }, [show, file?.id])

  const init = async () => {
    if (!file || !file.id) {
      handleClose()
      return
    }

    try {
      setIsLoading(true)
      const [notificationsData, usersGroupsData] = await Promise.all([
        getNotificationForFsi(file.id),
        context.api.legacy.usersApiService.getAssembledUsersGroups()
      ])

      setAvailableUsersGroups(usersGroupsData.merged)

      const assembledNotifications = {
        user: [] as (User | Group)[],
        system: [] as (User | Group)[],
        applyRecursively: false,
        applyOnlyChanges: false
      }

      // Assemble system notifications
      assembleForUI(
        notificationsData.systemGroupIds,
        assembledNotifications.system,
        SYSTEM_HELPER_TYPE.GROUP,
        usersGroupsData.merged
      )
      assembleForUI(
        notificationsData.systemUserIds,
        assembledNotifications.system,
        SYSTEM_HELPER_TYPE.USER,
        usersGroupsData.merged
      )

      // Assemble user notifications
      assembleForUI(
        notificationsData.userGroupIds,
        assembledNotifications.user,
        SYSTEM_HELPER_TYPE.GROUP,
        usersGroupsData.merged
      )
      assembleForUI(
        notificationsData.userUserIds,
        assembledNotifications.user,
        SYSTEM_HELPER_TYPE.USER,
        usersGroupsData.merged
      )

      setNotifications(assembledNotifications)
      setOriginalNotifications(JSON.parse(JSON.stringify(assembledNotifications)))
    } catch (error) {
      console.error('Error loading notifications:', error)
      toast.error('Failed to load notifications')
    } finally {
      setIsLoading(false)
    }
  }

  const assembleForUI = (
    from: number[],
    to: (User | Group)[],
    systemHelperType: string,
    availableData: (User | Group)[]
  ) => {
    from.forEach((id) => {
      const found = availableData.find((item) => item.id === id && item.systemHelperType === systemHelperType)
      if (found) {
        to.push(found)
      }
    })
  }

  const assembleForBE = (data: (User | Group)[], systemHelperType: string): number[] => {
    return data.filter((item) => item.systemHelperType === systemHelperType).map((item) => item.id)
  }

  const showValues = (arr: (User | Group)[]): string => {
    if (Array.isArray(arr) && arr.length > 0) {
      return arr.map((item) => item.chName || item.name || '').join(', ')
    }
    return '—'
  }

  const dataChanged = (): boolean => {
    return JSON.stringify(notifications) !== JSON.stringify(originalNotifications)
  }

  const cancel = () => {
    setNotifications(JSON.parse(JSON.stringify(originalNotifications)))
  }

  const save = async () => {
    if (!file || !file.id) return

    try {
      const saveData = {
        groupIds: assembleForBE(notifications.user, SYSTEM_HELPER_TYPE.GROUP),
        userIds: assembleForBE(notifications.user, SYSTEM_HELPER_TYPE.USER),
        applyRecursively: notifications.applyRecursively,
        applyOnlyChanges: notifications.applyOnlyChanges
      }

      await saveNotificationForFsi(file.id, saveData)

      // Emit events for table updates
      if (context.bus) {
        context.bus.dispatchEvent(new CustomEvent('bctable:updateTable', { detail: true }))
        context.bus.dispatchEvent(new CustomEvent('updateFiles', { detail: true }))
      }

      toast.success(translate('FOLDER_NOTIFICATIONS_UPDATED_MSG'))
      handleClose()
    } catch (error) {
      console.error('Error saving notifications:', error)
      toast.error('Failed to save notifications')
    }
  }

  const dismiss = () => {
    handleClose()
  }

  if (!show || !file) return null

  return (
    <Modal show={show} handleClose={dismiss} className="theme-sandman aclsPage" id="modal-add-file">
      <div className="modal-header">
        <h3 className="modal-title">
          {translate('NOTIFICATIONS_TITLE')}{' '}
          <button
            className="btn-show-tooltip"
            type="button"
            data-toggle="popover"
            data-trigger="hover"
            data-placement="bottom"
            data-html="true"
            data-content={translate('FSI_NOTIFICATIONS_TITLE_HOVER_INFO')}
            onMouseEnter={(e) => {
              const button = e.currentTarget
              const rect = button.getBoundingClientRect()
              const popover = document.createElement('div')
              popover.className = 'theme-sandman popover bottom in'
              popover.setAttribute('data-popover', 'true')
              popover.style.cssText = `position: fixed; top: ${rect.bottom + 5}px; left: ${rect.left + rect.width / 2}px; transform: translateX(-50%); display: block; max-width: 276px; padding: 1px; z-index: 1060;`
              popover.innerHTML = `
                <div class="arrow" style="position: absolute; top: -11px; left: 50%; margin-left: -11px; width: 0; height: 0; border-left: 11px solid transparent; border-right: 11px solid transparent; border-bottom: 11px solid #ccc;"></div>
                <div class="popover-content" style="padding: 9px 14px; font-size: 12px; line-height: 1.42857143; color: #333; background-color: #fff; border: 1px solid #ccc; border-radius: 4px; box-shadow: 0 5px 10px rgba(0,0,0,.2);">${translate('FSI_NOTIFICATIONS_TITLE_HOVER_INFO')}</div>
              `
              document.body.appendChild(popover)
              button.setAttribute('data-popover-id', 'active')
            }}
            onMouseLeave={(e) => {
              if (e.currentTarget.getAttribute('data-popover-id')) {
                const popover = document.body.querySelector('.popover[data-popover="true"]')
                popover?.remove()
                e.currentTarget.removeAttribute('data-popover-id')
              }
            }}
          />
        </h3>
        <i className="close-modal" onClick={dismiss} />
      </div>

      <Modal.Body>
        {isLoading ? (
          <div className="text-center py-4">Loading...</div>
        ) : (
          <>
            <div className="form-group md-form-group wparag w100p" style={{ marginBottom: '12px' }}>
              <label style={{ fontSize: '12px' }}>
                {file.type === FILE_TYPE.FOLDER ? translate('FOLDER') : translate('DOCUMENT')}
              </label>
              <p className="whitespaceprewrap" style={{ fontSize: '12px', marginTop: '0', marginBottom: '0' }}>
                {file.name}
              </p>
            </div>

            <table className="acl-table">
              <thead>
                <tr>
                  <th className="accessrights">{translate('NOTIFICATIONS_SUBSCRIPTION_TYPE')}</th>
                  <th className="usergroup" style={{ paddingLeft: '8px' }}>
                    {translate('NOTIFICATIONS_SUBSCRIBERS')}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ lineHeight: '14px' }}>
                  <td className="accessrights">{translate('NOTIFICATIONS_SYSTEM_MANAGED')}</td>
                  <td className="usergroup">
                    <EditableField value={showValues(notifications.system)} isDisabled={true}>
                      <SelectUserGroup
                        name="system"
                        value={notifications.system.map((item) => item._uid || '')}
                        onChange={() => {}}
                        placeholder={translate('SELECT_USERS/GROUPS...')}
                        isMulti={true}
                        isDisabled={true}
                      />
                    </EditableField>
                  </td>
                </tr>
                <tr style={{ lineHeight: '14px' }}>
                  <td className="accessrights">{translate('NOTIFICATIONS_USER_MANAGED')}</td>
                  <td className="usergroup">
                    <EditableField value={showValues(notifications.user)}>
                      <SelectUserGroup
                        name="user"
                        value={notifications.user.map((item) => item._uid || '')}
                        onChange={(value) => {
                          const selected = Array.isArray(value) ? value : [value]
                          const newUser = availableUsersGroups.filter((ug) => selected.includes(ug._uid || ''))
                          setNotifications({ ...notifications, user: newUser })
                        }}
                        placeholder={translate('SELECT_USERS/GROUPS...')}
                        isMulti={true}
                      />
                    </EditableField>
                  </td>
                </tr>
              </tbody>
            </table>

            {file.type === FILE_TYPE.FOLDER && (
              <>
                <div style={{ marginTop: '18px' }}>
                  <label>{translate('ACL_LABEL_APPLIES_TO')}</label>
                  <div className="form-group checkbox-form-group" style={{ marginBottom: '7px' }}>
                    <input
                      type="radio"
                      id="input-oaat3"
                      name="oatat3"
                      checked={!notifications.applyRecursively}
                      onChange={() => setNotifications({ ...notifications, applyRecursively: false })}
                    />
                    <label htmlFor="input-oaat3" style={{ fontWeight: 'normal', fontSize: '12px' }}>
                      <div className="chkbc" />
                      {translate('ACL_APPLY_TO_THIS_FOLDER_ONLY')}
                    </label>
                  </div>
                  <div className="form-group checkbox-form-group">
                    <input
                      type="radio"
                      id="input-ato3"
                      name="oatat3"
                      checked={notifications.applyRecursively}
                      onChange={() => setNotifications({ ...notifications, applyRecursively: true })}
                    />
                    <label htmlFor="input-ato3" style={{ fontWeight: 'normal', fontSize: '12px' }}>
                      <div className="chkbc" />
                      {translate('ACL_APPLY_TO_THIS_FOLDER_AND_ALL_ENCLOSED_FOLDERS')}
                    </label>
                  </div>
                </div>
                {notifications.applyRecursively && (
                  <div className="form-group checkbox-form-group small-sub" style={{ marginTop: '21px' }}>
                    <input
                      type="checkbox"
                      id="onlyApplyChangesId"
                      name="onlyApplyChangesId"
                      checked={notifications.applyOnlyChanges}
                      onChange={(e) => setNotifications({ ...notifications, applyOnlyChanges: e.target.checked })}
                    />
                    <label htmlFor="onlyApplyChangesId" style={{ fontSize: '14px' }}>
                      <div className="chkbc" />
                      {translate('ACL_ONLY_APPLY_CHANGES')}
                    </label>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Modal.Body>

      <div className="modal-footer">
        {dataChanged() && (
          <>
            <button className="btn btn-close" type="button" onClick={cancel}>
              {translate('CANCEL')}
            </button>
            <button className="btn btn-primary at_btnSaveAcls" type="button" onClick={save}>
              {translate('SAVE')}
            </button>
          </>
        )}
        {!dataChanged() && (
          <button className="btn btn-primary" type="button" onClick={dismiss}>
            {translate('OK')}
          </button>
        )}
      </div>
    </Modal>
  )
}

export default FsiNotificationsModal
